import cloneDeep from 'lodash.clonedeep';

import baseView from '../base-view';

const baseViewFn = baseView.make();
const form = baseView.make();

form.request = null;

form.getField = function(name) {
  return this.el.querySelector(`[data-field=${name}]`);
};

form.getFields = function() {
  return this.el.querySelectorAll('[data-field]');
};

form.isDisabled = function() {
  return this.isRendered() && this.el.classList.contains('disabled');
};

form.getFieldValues = function() {
  const fields = this.getFields();

  const setValue = function(data, field) {
    const name = field.getAttribute('data-field');
    const value = field.type === 'checkbox' ? field.checked : field.value;

    data[name] = value;

    return data;
  };

  return Array.prototype.reduce.call(fields, setValue, {});
};

form.serialize = function() {
  return Promise.resolve(this.getFieldValues());
};

form.findFieldAlert = function(id) {
  return this.el.querySelector(`[role="alert"][data-for="${id}"]`);
};

form.findStatusAlert = function(status) {
  const selector = `[role="alert"][data-status~="${status}"]`;
  let alert = this.el.querySelector(selector);

  if (!alert) {
    alert = this.el.querySelector('[role="alert"][data-status="*"]');
  }

  return alert;
};

const showAlert = function(alert) {
  alert.classList.add('visible');
};

const hideAlert = function(alert) {
  alert.classList.remove('visible');
};

form.validate = function() {
  return this.el.checkValidity();
};

form.handleRequestError = function(error) {
  if (error.response) {
    const alert = this.findStatusAlert(error.response.status);

    if (alert) {
      this.enable();
      showAlert(alert);
      return;
    }
  }

  throw error;
};

form.sendRequest = function() {
  const errorHandler = (error) => {
    this.dispatchEvent('request:error');

    return this.handleRequestError(error);
  };

  const dispatchSuccess = () => {
    return this.dispatchEvent('request:success');
  };

  const send = (data) => {
    this.dispatchEvent('request:sent');

    return this.request(data);
  };

  return this.serialize()
    .then(send)
    .then(dispatchSuccess)
    .catch(errorHandler);
};

form.onSubmit = function(event) {
  event.preventDefault();

  if (!this.isDisabled() && this.validate()) {
    this.sendRequest();
  }
};

form.onInvalid = function(event) {
  const alert = this.findFieldAlert(event.target.id);

  if (alert) {
    // Prevent the display of native validation popovers
    event.preventDefault();
    showAlert(alert);
  }
};

form.onUpdate = function(event) {
  const el = event.target;

  if (el.validity && !el.validity.valid) {
    return;
  }

  const alert = this.findFieldAlert(el.id);

  if (alert) {
    hideAlert(alert);
  }
};

form.render = function() {
  const addListeners = () => {
    this.addEventListener('blur', this.onUpdate.bind(this), true);
    this.addEventListener('change', this.onUpdate.bind(this), true);
    this.addEventListener('invalid', this.onInvalid.bind(this), true);
    this.addEventListener('request:error', this.enable.bind(this));
    this.addEventListener('request:sent', this.disable.bind(this));
    this.addEventListener('request:success', this.enable.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
  };

  return baseViewFn.render.call(this)
    .then(addListeners);
};

form.getSubmitButton = function() {
  return this.el.querySelector('button[type=submit]');
};

form.enable = function() {
  if (!this.isRendered()) {
    throw new Error(`View isn't rendered`);
  }

  this.el.classList.remove('disabled');

  const submit = this.getSubmitButton();

  if (submit) {
    submit.removeAttribute('disabled');
  }
};

form.disable = function() {
  if (!this.isRendered()) {
    throw new Error(`View isn't rendered`);
  }

  this.el.classList.add('disabled');

  const submit = this.getSubmitButton();

  if (submit) {
    submit.setAttribute('disabled', '');
  }
};

const make = cloneDeep.bind(null, form);

export default {
  make,
};
